import { useState, useMemo } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import {
  Box,
  Typography,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  Paper,
  Stack,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import BuildIcon from '@mui/icons-material/Build';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import SecurityIcon from '@mui/icons-material/Security';
import HeadsetMicIcon from '@mui/icons-material/HeadsetMic';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import UnfoldLessIcon from '@mui/icons-material/UnfoldLess';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import { useLanguage } from '../context/LanguageContext';

const ACCENT_COLOR = '#FF8C42';
const ACCENT_LIGHT = '#FFF5EF';
const TEXT_PRIMARY = '#1E3A45';
const TEXT_SECONDARY = '#6B7280';
const BORDER_COLOR = '#E5E7EB';

function ImageUploadValidationCard() {
  return (
    <Paper
      elevation={0}
      sx={{
        mt: 2,
        p: 2.5,
        borderRadius: '12px',
        bgcolor: '#FFFFFF',
        border: '1px solid #E5E7EB',
        boxShadow: '0 2px 10px rgba(0,0,0,0.03)',
      }}
    >
      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1E3A45', mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
        <CloudUploadIcon sx={{ color: '#FF8C42', fontSize: 20 }} />
        Image Upload Requirements & Validation Rules
      </Typography>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
        <Box sx={{ flex: 1, p: 1.5, borderRadius: '8px', bgcolor: '#FFF5EF', border: '1px solid rgba(255, 140, 66, 0.2)' }}>
          <Typography variant="caption" sx={{ fontWeight: 700, color: '#E06526', textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', mb: 0.5 }}>
            Allowed Image Formats
          </Typography>
          <Stack direction="row" spacing={0.8} flexWrap="wrap">
            {['PNG', 'JPG', 'JPEG', 'WEBP'].map((fmt) => (
              <Chip key={fmt} label={fmt} size="small" sx={{ bgcolor: '#FF8C42', color: '#fff', fontWeight: 700, fontSize: '0.7rem', height: 20 }} />
            ))}
          </Stack>
        </Box>

        <Box sx={{ flex: 1, p: 1.5, borderRadius: '8px', bgcolor: '#F3F4F6', border: '1px solid #E5E7EB' }}>
          <Typography variant="caption" sx={{ fontWeight: 700, color: '#4B5563', textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', mb: 0.5 }}>
            Size & Dimension Limits
          </Typography>
          <Typography variant="body2" sx={{ fontSize: '0.825rem', color: '#1F2937', fontWeight: 600 }}>
            Max File Size: 10 MB
          </Typography>
          <Typography variant="caption" sx={{ color: '#6B7280', display: 'block' }}>
            Required Dimensions: 355×355 px
          </Typography>
        </Box>
      </Stack>

      <Box sx={{ p: 1.5, borderRadius: '8px', bgcolor: '#FAFAFA', border: '1px dashed #D1D5DB' }}>
        <Typography variant="caption" sx={{ fontWeight: 700, color: '#374151', display: 'block', mb: 0.5 }}>
          Instant Validation Rules
        </Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 1, sm: 2 }} flexWrap="wrap">
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <CheckCircleIcon sx={{ fontSize: 16, color: '#10B981' }} />
            <Typography variant="caption" sx={{ color: '#047857', fontWeight: 600 }}>
              Valid image & size &le; 10MB
            </Typography>
          </Stack>
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <ErrorIcon sx={{ fontSize: 16, color: '#EF4444' }} />
            <Typography variant="caption" sx={{ color: '#B91C1C', fontWeight: 600 }}>
              Non-image or size &gt; 10MB (Rejected)
            </Typography>
          </Stack>
        </Stack>
      </Box>
    </Paper>
  );
}

export default function Faq() {
  const navigate = useNavigate();
  const outletContext = useOutletContext();
  const setHelpCenterOpen = outletContext?.setHelpCenterOpen;
  const { t } = useLanguage();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [selectedCategory, setSelectedCategory] = useState('all');
  const [expandedPanel, setExpandedPanel] = useState(false);

  // FAQ Categories metadata
  const categories = useMemo(
    () => [
      { id: 'all', label: t('pages.faq.categories.all'), icon: <HelpOutlineIcon fontSize="small" /> },
      { id: 'account', label: t('pages.faq.categories.account'), icon: <AccountCircleIcon fontSize="small" /> },
      { id: 'services', label: t('pages.faq.categories.services'), icon: <BuildIcon fontSize="small" /> },
      { id: 'bookings', label: t('pages.faq.categories.bookings'), icon: <EventAvailableIcon fontSize="small" /> },
      { id: 'promotions', label: t('pages.faq.categories.promotions'), icon: <LocalOfferIcon fontSize="small" /> },
      { id: 'support', label: t('pages.faq.categories.support'), icon: <SecurityIcon fontSize="small" /> },
    ],
    [t]
  );

  // FAQ items list with i18n keys
  const faqItems = useMemo(
    () => [
      {
        id: 'faq-2',
        category: 'account',
        question: t('pages.faq.items.q2.question'),
        answer: t('pages.faq.items.q2.answer'),
      },
      {
        id: 'faq-3',
        category: 'services',
        question: t('pages.faq.items.q3.question'),
        answer: t('pages.faq.items.q3.answer'),
      },
      {
        id: 'faq-4',
        category: 'services',
        question: t('pages.faq.items.q4.question'),
        answer: t('pages.faq.items.q4.answer'),
      },
      {
        id: 'faq-5',
        category: 'bookings',
        question: t('pages.faq.items.q5.question'),
        answer: t('pages.faq.items.q5.answer'),
      },
      {
        id: 'faq-6',
        category: 'bookings',
        question: t('pages.faq.items.q6.question'),
        answer: t('pages.faq.items.q6.answer'),
      },
      {
        id: 'faq-7',
        category: 'promotions',
        question: t('pages.faq.items.q7.question'),
        answer: t('pages.faq.items.q7.answer'),
      },
      {
        id: 'faq-8',
        category: 'promotions',
        question: t('pages.faq.items.q8.question'),
        answer: t('pages.faq.items.q8.answer'),
      },
      {
        id: 'faq-9',
        category: 'support',
        question: t('pages.faq.items.q9.question'),
        answer: t('pages.faq.items.q9.answer'),
      },
      {
        id: 'faq-10',
        category: 'support',
        question: t('pages.faq.items.q10.question'),
        answer: t('pages.faq.items.q10.answer'),
      },
      {
        id: 'faq-11',
        category: 'services',
        question: t('pages.faq.items.q11.question'),
        answer: t('pages.faq.items.q11.answer'),
        showImageValidationCard: true,
      },
    ],
    [t]
  );

  // Filter items based on selected category
  const filteredFaqs = useMemo(() => {
    return faqItems.filter((item) => {
      return selectedCategory === 'all' || item.category === selectedCategory;
    });
  }, [faqItems, selectedCategory]);

  const handleAccordionChange = (panelId) => (event, isExpanded) => {
    setExpandedPanel(isExpanded ? panelId : false);
  };

  const handleExpandAll = () => {
    setExpandedPanel('all');
  };

  const handleCollapseAll = () => {
    setExpandedPanel(false);
  };

  return (
    <Box sx={{ maxWidth: 1100, mx: 'auto', p: { xs: 2, sm: 3, md: 4 } }}>
      {/* Header Banner Card */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 3, sm: 4 },
          mb: 4,
          borderRadius: 4,
          background: 'linear-gradient(135deg, #ffffff 0%, #FFF5EF 100%)',
          border: `1px solid rgba(255, 140, 66, 0.3)`,
          boxShadow: '0 4px 20px rgba(255, 140, 66, 0.08)',
        }}
      >
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} alignItems={{ md: 'center' }} justifyContent="space-between">
          <Box sx={{ maxWidth: 650 }}>
            <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1 }}>
              <Box
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: '12px',
                  bgcolor: ACCENT_COLOR,
                  color: '#fff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 4px 10px rgba(255, 140, 66, 0.3)',
                }}
              >
                <HelpOutlineIcon sx={{ fontSize: 26 }} />
              </Box>
              <Typography variant="h4" sx={{ fontWeight: 700, color: TEXT_PRIMARY, fontSize: { xs: '1.5rem', sm: '1.875rem' } }}>
                {t('pages.faq.title')}
              </Typography>
            </Stack>
            <Typography variant="body1" sx={{ color: TEXT_SECONDARY, lineHeight: 1.6, mt: 0.5 }}>
              {t('pages.faq.subtitle')}
            </Typography>
          </Box>
        </Stack>
      </Paper>

      {/* Category Chips Bar & Controls */}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ sm: 'center' }}
        spacing={2}
        sx={{ mb: 3 }}
      >
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {categories.map((cat) => {
            const isSelected = selectedCategory === cat.id;
            return (
              <Chip
                key={cat.id}
                icon={cat.icon}
                label={cat.label}
                onClick={() => setSelectedCategory(cat.id)}
                sx={{
                  px: 1,
                  py: 2.2,
                  borderRadius: '12px',
                  fontWeight: isSelected ? 600 : 500,
                  fontSize: '0.875rem',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  backgroundColor: isSelected ? ACCENT_COLOR : '#fff',
                  color: isSelected ? '#fff' : TEXT_PRIMARY,
                  border: `1px solid ${isSelected ? ACCENT_COLOR : BORDER_COLOR}`,
                  '& .MuiChip-icon': {
                    color: isSelected ? '#fff' : ACCENT_COLOR,
                  },
                  '&:hover': {
                    backgroundColor: isSelected ? '#E06526' : ACCENT_LIGHT,
                    borderColor: ACCENT_COLOR,
                  },
                }}
              />
            );
          })}
        </Box>

        {/* Expand / Collapse All */}
        {filteredFaqs.length > 0 && (
          <Stack direction="row" spacing={1} sx={{ flexShrink: 0, alignSelf: { xs: 'flex-end', sm: 'center' } }}>
            <Button
              size="small"
              startIcon={<UnfoldMoreIcon />}
              onClick={handleExpandAll}
              sx={{ color: TEXT_SECONDARY, textTransform: 'none', '&:hover': { color: ACCENT_COLOR } }}
            >
              {t('pages.faq.expandAll')}
            </Button>
            <Button
              size="small"
              startIcon={<UnfoldLessIcon />}
              onClick={handleCollapseAll}
              sx={{ color: TEXT_SECONDARY, textTransform: 'none', '&:hover': { color: ACCENT_COLOR } }}
            >
              {t('pages.faq.collapseAll')}
            </Button>
          </Stack>
        )}
      </Stack>

      {/* Accordion FAQ Items */}
      {filteredFaqs.length === 0 ? (
        <Paper
          elevation={0}
          sx={{
            p: 6,
            textAlign: 'center',
            borderRadius: 4,
            border: `1px dashed ${BORDER_COLOR}`,
            bgcolor: '#FAFAFA',
          }}
        >
          <HelpOutlineIcon sx={{ fontSize: 56, color: '#D1D5DB', mb: 2 }} />
          <Typography variant="h6" sx={{ color: TEXT_PRIMARY, fontWeight: 600, mb: 1 }}>
            {t('pages.faq.noResultsTitle')}
          </Typography>
          <Typography variant="body2" sx={{ color: TEXT_SECONDARY, maxWidth: 450, mx: 'auto', mb: 3 }}>
            {t('pages.faq.noResultsSubtitle')}
          </Typography>
          <Button
            variant="outlined"
            onClick={() => {
              setSelectedCategory('all');
            }}
            sx={{
              borderColor: ACCENT_COLOR,
              color: ACCENT_COLOR,
              borderRadius: '10px',
              textTransform: 'none',
              fontWeight: 600,
              '&:hover': { borderColor: '#E06526', bgcolor: ACCENT_LIGHT },
            }}
          >
            {t('pages.faq.clearFilters')}
          </Button>
        </Paper>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {filteredFaqs.map((faq) => {
            const isExpanded = expandedPanel === 'all' || expandedPanel === faq.id;

            return (
              <Accordion
                key={faq.id}
                expanded={isExpanded}
                onChange={handleAccordionChange(faq.id)}
                elevation={0}
                sx={{
                  borderRadius: '14px !important',
                  border: `1px solid ${isExpanded ? ACCENT_COLOR : BORDER_COLOR}`,
                  transition: 'all 0.2s ease-in-out',
                  overflow: 'hidden',
                  '&:before': { display: 'none' },
                  boxShadow: isExpanded ? '0 4px 14px rgba(255, 140, 66, 0.12)' : '0 1px 3px rgba(0,0,0,0.03)',
                  bgcolor: '#ffffff',
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon sx={{ color: isExpanded ? ACCENT_COLOR : TEXT_SECONDARY }} />}
                  sx={{
                    px: { xs: 2, sm: 3 },
                    py: 1,
                    '&:hover': { bgcolor: ACCENT_LIGHT },
                  }}
                >
                  <Typography
                    sx={{
                      fontWeight: 600,
                      color: isExpanded ? ACCENT_COLOR : TEXT_PRIMARY,
                      fontSize: { xs: '0.95rem', sm: '1.025rem' },
                      py: 0.5,
                    }}
                  >
                    {faq.question}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails
                  sx={{
                    px: { xs: 2, sm: 3 },
                    pb: 2.5,
                    pt: 0.5,
                    borderTop: `1px solid ${BORDER_COLOR}`,
                    bgcolor: '#FAFBFD',
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{
                      color: '#374151',
                      lineHeight: 1.7,
                      fontSize: '0.95rem',
                      whiteSpace: 'pre-line',
                      pt: 1,
                    }}
                  >
                    {faq.answer}
                  </Typography>

                  {/* Optional Image Upload Rules & Validation Card */}
                  {faq.showImageValidationCard && <ImageUploadValidationCard />}

                  {/* Optional Custom Image Attachment */}
                  {faq.image && (
                    <Box
                      sx={{
                        mt: 2,
                        p: 1.5,
                        borderRadius: '12px',
                        bgcolor: '#FFFFFF',
                        border: `1px solid ${BORDER_COLOR}`,
                        boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                        maxWidth: '100%',
                      }}
                    >
                      <Box
                        component="img"
                        src={faq.image}
                        alt={faq.question}
                        sx={{
                          width: '100%',
                          maxHeight: 340,
                          objectFit: 'contain',
                          borderRadius: '8px',
                          display: 'block',
                        }}
                      />
                      {faq.imageCaption && (
                        <Typography
                          variant="caption"
                          sx={{ color: TEXT_SECONDARY, display: 'block', mt: 1, textAlign: 'center', fontStyle: 'italic' }}
                        >
                          {faq.imageCaption}
                        </Typography>
                      )}
                    </Box>
                  )}
                </AccordionDetails>
              </Accordion>
            );
          })}
        </Box>
      )}

      {/* Still Need Help Banner */}
      <Paper
        elevation={0}
        sx={{
          mt: 5,
          p: { xs: 3, sm: 4 },
          borderRadius: 4,
          bgcolor: TEXT_PRIMARY,
          color: '#ffffff',
          boxShadow: '0 8px 24px rgba(30, 58, 69, 0.15)',
        }}
      >
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={3}
          alignItems={{ sm: 'center' }}
          justifyContent="space-between"
        >
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
              {t('pages.faq.needHelpTitle')}
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.85, maxWidth: 500, lineHeight: 1.6 }}>
              {t('pages.faq.needHelpSubtitle')}
            </Typography>
          </Box>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ flexShrink: 0 }}>
            <Button
              variant="contained"
              startIcon={<HeadsetMicIcon />}
              onClick={() => {
                if (setHelpCenterOpen) {
                  setHelpCenterOpen(true);
                } else {
                  navigate('/enquiry');
                }
              }}
              sx={{
                bgcolor: ACCENT_COLOR,
                color: '#ffffff',
                fontWeight: 600,
                borderRadius: '10px',
                px: 2.5,
                py: 1.2,
                textTransform: 'none',
                boxShadow: '0 4px 12px rgba(255, 140, 66, 0.3)',
                '&:hover': { bgcolor: '#E06526' },
              }}
            >
              {t('pages.faq.helpCenter')}
            </Button>
          </Stack>
        </Stack>
      </Paper>
    </Box>
  );
}
